import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listConversations, setConversationStatus } from '../api/services/conversationService';
import { getWorkspaceSettings, saveWorkspaceSettings } from '../api/services/businessService';
import SettingsTabs from '../components/layout/SettingsTabs';
import { nameError } from '../utils/validation';

// Hosts the two simple workspace screens that don't have a dedicated module:
//   /conversations -> read-mostly list of every conversation in the workspace (shared with Agent Workspace)
//   /settings      -> workspace-level settings
// Both read and write through the API client; nothing here keeps its own storage.

const STATUS_OPTIONS = ['All', 'Open', 'Pending', 'Escalated', 'Closed', 'Resolved'];

function Heading({ title, notice, error, onDismiss }) {
  return <>
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div><h1 className="h4 fw-bold mb-1">{title}</h1><p className="text-muted small mb-0">Manage your {title.toLowerCase()}.</p></div>
    </div>
    {notice && <div className="alert alert-success py-2" role="status">{notice}<button className="btn-close float-end" aria-label="Dismiss" onClick={onDismiss} /></div>}
    {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
  </>;
}

function Conversations({ title }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('All');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    return listConversations().then(setItems).catch((err) => setError(err.message || 'Unable to load conversations.')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => items.filter((c) => {
    const text = `${c.id} ${c.customer} ${c.messages.map((m) => m.text).join(' ')}`.toLowerCase();
    return text.includes(query.toLowerCase()) && (selected === 'All' || c.status === selected);
  }), [items, query, selected]);

  const toggle = async (c) => {
    const closed = c.status === 'Closed' || c.status === 'Resolved';
    setError('');
    try {
      await setConversationStatus(c.id, closed ? 'Open' : 'Closed');
      setNotice(`Conversation ${c.customer} ${closed ? 'reopened' : 'closed'}.`);
      load();
    } catch (err) {
      setError(err.message || 'Unable to update the conversation.');
    }
  };

  const real = (c) => c.messages.filter((m) => m.from !== 'system');
  return <>
    <Heading title={title} notice={notice} error={error} onDismiss={() => setNotice('')} />
    <section className="cb-card p-3 p-md-4">
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ maxWidth: 320 }} aria-label="Search conversations" placeholder="Search conversations" value={query} onChange={(e) => setQuery(e.target.value)} />
        <select className="form-select" style={{ maxWidth: 160 }} aria-label="Filter by status" value={selected} onChange={(e) => setSelected(e.target.value)}>{STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      {loading ? <p className="text-muted mb-0">Loading conversations…</p> : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead><tr><th>ID</th><th>Visitor</th><th>Subject</th><th>Status</th><th>Last message</th><th>Action</th></tr></thead>
            <tbody>{filtered.map((c) => {
              const msgs = real(c);
              const first = msgs.find((m) => m.from === 'visitor');
              const last = msgs[msgs.length - 1];
              const closed = c.status === 'Closed' || c.status === 'Resolved';
              return <tr key={c.id}>
                <td>{c.id.slice(-8)}</td><td>{c.customer}</td><td>{first ? first.text.slice(0, 40) : '—'}</td>
                <td><span className="badge text-bg-light border">{c.status}</span></td><td>{last ? last.text.slice(0, 60) : '—'}</td>
                <td><button className="btn btn-sm btn-outline-primary" onClick={() => toggle(c)}>{closed ? 'Reopen' : 'Close'}</button></td>
              </tr>;
            })}</tbody>
          </table>
          {!filtered.length && <p className="text-muted mb-0">No conversations found.</p>}
        </div>
      )}
    </section>
  </>;
}

function Settings({ title }) {
  const [settings, setSettings] = useState({ workspace: '', timezone: 'Asia/Kolkata', emailAlerts: true, maintenance: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getWorkspaceSettings().then((s) => { if (active) setSettings(s); }).catch((err) => { if (active) setError(err.message || 'Unable to load settings.'); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const problem = nameError(settings.workspace, { label: 'Workspace name', max: 80 });
    if (problem) { setError(problem); return; }
    setError(''); setBusy(true);
    try { setSettings(await saveWorkspaceSettings(settings)); setNotice('Settings saved successfully.'); }
    catch (err) { setError(err.details?.fields?.workspace || err.message || 'Unable to save settings.'); }
    finally { setBusy(false); }
  };

  return <>
    <Heading title={title} notice={notice} error={error} onDismiss={() => setNotice('')} />
    <SettingsTabs />
    <section className="cb-card p-3 p-md-4">
      {loading ? <p className="text-muted mb-0">Loading settings…</p> : (
        <form noValidate onSubmit={submit}>
          <div className="mb-3"><label className="form-label" htmlFor="ws-name">Workspace name</label><input id="ws-name" className="form-control" value={settings.workspace} onChange={(e) => setSettings({ ...settings, workspace: e.target.value })} required /></div>
          <div className="mb-3"><label className="form-label" htmlFor="ws-timezone">Timezone</label><select id="ws-timezone" className="form-select" value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}><option>Asia/Kolkata</option><option>UTC</option><option>America/New_York</option><option>Europe/London</option></select></div>
          <div className="form-check mb-2"><input className="form-check-input" type="checkbox" id="emailAlerts" checked={settings.emailAlerts} onChange={(e) => setSettings({ ...settings, emailAlerts: e.target.checked })} /><label className="form-check-label" htmlFor="emailAlerts">Email notifications</label></div>
          <div className="form-check mb-3"><input className="form-check-input" type="checkbox" id="maintenance" checked={settings.maintenance} onChange={(e) => setSettings({ ...settings, maintenance: e.target.checked })} /><label className="form-check-label" htmlFor="maintenance">Maintenance mode</label></div>
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
        </form>
      )}
    </section>
  </>;
}

export default function PlaceholderPage({ title }) {
  const { pathname } = useLocation();
  if (pathname === '/conversations') return <Conversations title={title} />;
  if (pathname === '/settings') return <Settings title={title} />;
  return <section className="cb-card p-3 p-md-4"><h1 className="h4">{title}</h1><p className="mb-0">Select a section from the sidebar.</p></section>;
}
